// MACHINERY V2 (Stroje) — V4 shop-floor wall: the kiosk header (M2.14).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.14 + desktop/README.md §V4.
// Rendered truth: desktop/screenshots/v4-wall--refined-claude.png and
// v4-wall--machineid-native.png (both viewed before this file was written, per
// the view-before-code rule). Reference impl: desktop/machinery-v4-wall.jsx
// (V4WallHeader).
//
// Composition, left to right, exactly as the artboard draws it:
//   MACHINE-ID wordmark · divider · teal mono eyebrow + plant name (18/600) ·
//   spacer · zero-padded mono 36/700 status-coloured counters with status icons
//   · divider · live dot + ticking mono clock.
//
// THE ALARM COUNTER RENDERS ONLY WHEN > 0 (the ticket says so, and the artboard
// shows it present, at 01). A permanent "00 V ALARME" on a hall TV is a
// standing red numeral that people stop reading; its absence is the signal.
//
// COUNTS COME FROM THE SAME SET THE TILES DO — the caller passes
// countStates(<the machines it renders>, states). On a surface with no filter
// rail there is nothing to explain a discrepancy, so a header counting machines
// the grid below does not show would simply be wrong.
//
// SINCE THE M2.14 FIX PASS THAT SET IS THE WHOLE ACTIVE PARK: the wall renders
// `FleetPageData.flat`, the un-faceted base set the rail's own counts are
// computed over (useFleetPageData.ts:203, `countStates(flat, states)`, re-read
// at commit time). "Count what we render" and "count the plant" are now the
// same number; handed the FACETED `visibleFlat` they were not, and a header
// reading 01 V ALARME over a hall of five was self-consistent and false.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import StatusIcon from "../StatusIcon";
import { MachineryV2IconKind, MachineryV2StatusToken } from "../cardStates";
import WallLiveClock from "./WallLiveClock";

interface WallCounterSpec {
  /** Sum of these canonical states (M2.1 contract names). */
  states: string[];
  token: MachineryV2StatusToken;
  icon: MachineryV2IconKind;
  labelKey: string;
  /** Hide the counter entirely at zero (alarm only). */
  hideWhenZero?: boolean;
}

/**
 * The artboard's five counters, mapped onto the 9-state contract.
 *
 * `stale-alarm` is summed into the alarm counter ON PURPOSE: a machine whose
 * alarm reading is last-known is still a machine in alarm, and the wall's job
 * is to make someone walk over. The TILE keeps the distinction (it desaturates
 * and prints the last-known prefix); the counter does not need two numerals for
 * one problem.
 *
 * `unconnected`, `awaiting` and `stale` get NO counter, exactly as the artboard
 * has none: on a real fleet ~99% of machines are unconnected, so a sixth
 * numeral in the hundreds would dwarf the five that matter. All three are still
 * rendered as tiles below.
 */
const WALL_COUNTERS: readonly WallCounterSpec[] = [
  {
    states: ["alarm", "stale-alarm"],
    token: "--red",
    icon: "alert",
    labelKey: "myMachinery:v2.fleet.wall.cAlarm",
    hideWhenZero: true,
  },
  {
    states: ["running"],
    token: "--green",
    icon: "play",
    labelKey: "myMachinery:v2.fleet.wall.cRunning",
  },
  {
    states: ["idle"],
    token: "--amber",
    icon: "pause",
    labelKey: "myMachinery:v2.fleet.wall.cIdle",
  },
  {
    states: ["stopped"],
    token: "--muted",
    icon: "square",
    labelKey: "myMachinery:v2.fleet.wall.cStopped",
  },
  {
    states: ["offline"],
    token: "--faint",
    icon: "cloudoff",
    labelKey: "myMachinery:v2.fleet.wall.cOffline",
  },
];

const Divider: React.FC = () => (
  <span style={{ height: 22, width: 1, background: "var(--border)", flexShrink: 0 }} />
);

/** Wordmark. Inlined rather than lifted out of ChromeNavV4: that one is mono
 *  14/700 sized for a 52px bar, this one is sans 18/800 for a kiosk header, and
 *  two short blocks beat one helper carrying a size matrix. */
const WallWordmark: React.FC = () => (
  <div
    style={{
      fontWeight: 800,
      fontSize: 18,
      color: "var(--ink)",
      letterSpacing: "0.02em",
      whiteSpace: "nowrap",
      flexShrink: 0,
    }}
  >
    MACHINE
    <b
      style={{
        background: "var(--ink)",
        color: "var(--card)",
        padding: "3px 6px",
        borderRadius: 5,
        marginLeft: 2,
        fontSize: 14,
      }}
    >
      ID
    </b>
  </div>
);

const WallCounter: React.FC<{ n: number; label: string; spec: WallCounterSpec }> = ({
  n,
  label,
  spec,
}) => {
  const color = "var(" + spec.token + ")";
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
      {/* Zero-padded to two digits as the artboard prints it — padStart never
          truncates, so a three-digit fleet count still prints all three. */}
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 36,
          color,
          fontWeight: 700,
          letterSpacing: "-0.02em",
          lineHeight: 1,
        }}
      >
        {String(n).padStart(2, "0")}
      </span>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          color: "var(--ink-2)",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          opacity: 0.75,
          whiteSpace: "nowrap",
        }}
      >
        <span style={{ color, display: "inline-flex" }}>
          <StatusIcon kind={spec.icon} size={11} />
        </span>
        {label}
      </span>
    </div>
  );
};

export interface WallHeaderProps {
  /** Plant / zavod line, derived from the rendered machines' locations. */
  plant: string;
  /** Already-translated "+N more", or null when every plant name is printed.
   *  A SEPARATE field, not appended to `plant`: glued on with the same " + "
   *  separator the names use, a bare numeral read as a site called "3". */
  plantMore: string | null;
  /** countStates() over the machines the grid renders. */
  counts: Record<string, number>;
  /** Poll health for the liveness dot — see WallLiveClock. */
  lastTelemetryOkAt: number;
}

const WallHeader: React.FC<WallHeaderProps> = ({
  plant,
  plantMore,
  counts,
  lastTelemetryOkAt,
}) => {
  const { translate } = useTranslation("myMachinery");

  return (
    <header
      style={{
        padding: "18px 28px",
        borderBottom: "1px solid var(--border)",
        background: "var(--card)",
        display: "flex",
        alignItems: "center",
        // flexWrap + rowGap are OURS, not the artboard's: it is drawn at a fixed
        // 1920 where nothing can collide. Below roughly 1500 the five counters
        // and the clock would otherwise crush the plant name. Wrapping is the
        // graceful degradation; at the 1920 design width nothing wraps.
        gap: 28,
        rowGap: 12,
        flexWrap: "wrap",
        flexShrink: 0,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
        <WallWordmark />
        <Divider />
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10.5,
              color: "var(--mod-accent)",
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              fontWeight: 700,
            }}
          >
            {translate("myMachinery:v2.fleet.wall.eyebrow")}
          </div>
          <div
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: "var(--ink)",
              marginTop: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {plant}
            {plantMore && (
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 12.5,
                  fontWeight: 500,
                  color: "var(--ink-2)",
                  opacity: 0.7,
                  marginLeft: 8,
                }}
              >
                {plantMore}
              </span>
            )}
          </div>
        </div>
      </div>

      <span style={{ flex: 1, minWidth: 0 }} />

      <div
        style={{
          display: "flex",
          gap: 22,
          rowGap: 8,
          flexWrap: "wrap",
          alignItems: "baseline",
        }}
      >
        {WALL_COUNTERS.map((spec) => {
          const n = spec.states.reduce((sum, s) => sum + (counts[s] || 0), 0);
          if (spec.hideWhenZero && n === 0) return null;
          return (
            <WallCounter key={spec.labelKey} n={n} label={translate(spec.labelKey)} spec={spec} />
          );
        })}
      </div>

      <Divider />

      {/* The dot + clock are a LEAF (WallLiveClock) for two reasons: the 1 Hz
          tick must not re-render the grid, and the dot is driven by real poll
          health rather than a hardcoded "live". Both in that file's header. */}
      <WallLiveClock lastTelemetryOkAt={lastTelemetryOkAt} />
    </header>
  );
};

export default WallHeader;
